import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  AlertTriangle,
  MapPin,
  MessageCircle,
  UserPlus,
  Lightbulb,
  Zap,
  CheckCircle,
  Radio,
} from "lucide-react";
import { useTranslation } from "react-i18next";

interface EmergencyContactsWidgetProps {
  color?: string;
}

const EmergencyContactsWidget: React.FC<EmergencyContactsWidgetProps> = ({
  color = "from-rose-400 to-red-600",
}) => {
  const { t } = useTranslation("nonHealth");
  
  const contacts = [
    { name: t("widgets.emergencyContacts.mom"), phone: "۰۹۱۲-***-۱۲۳۴", avatar: "م", color: "bg-rose-400", primary: true },
    { name: t("widgets.emergencyContacts.dad"), phone: "۰۹۱۱-***-۵۶۷۸", avatar: "پ", color: "bg-blue-500", primary: false },
    { name: t("widgets.emergencyContacts.school"), phone: "۰۲۱-***-۹۰۱۲", avatar: "م", color: "bg-amber-500", primary: false },
  ];

  const [sosActivated, setSosActivated] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSOS = () => {
    if (sending || sosActivated) return;
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setSosActivated(true);
      setTimeout(() => setSosActivated(false), 4000);
    }, 2000);
  };

  return (
    <motion.div
      key="emergencyContacts-widget"
      className="relative w-full max-w-sm"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5, type: "spring", stiffness: 100 }}
    >
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-5 shadow-xl border border-rose-200 relative overflow-hidden">
        {/* افکت‌های پس‌زمینه */}
        <motion.div
          className="absolute -left-10 top-10 w-40 h-40 bg-gradient-to-br from-rose-400/15 to-red-600/10 rounded-full blur-2xl"
          animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 6, repeat: Infinity }}
        />
        {sosActivated && (
          <motion.div
            className="absolute inset-0 bg-red-500/10 z-10 pointer-events-none"
            animate={{ opacity: [0, 0.3, 0] }}
            transition={{ duration: 0.5, repeat: Infinity }}
          />
        )}

        {/* هدر */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <motion.div
              className={`w-9 h-9 rounded-full bg-gradient-to-r ${color} flex items-center justify-center text-white shadow-md`}
              animate={sosActivated ? { scale: [1, 1.2, 1], rotate: [0, -10, 10, 0] } : { scale: [1, 1.05, 1] }}
              transition={sosActivated ? { duration: 0.5, repeat: Infinity } : { duration: 2, repeat: Infinity }}
            >
              <Phone className="w-5 h-5" />
            </motion.div>
            <h5 className="text-rose-800 font-semibold text-lg">{t("widgets.emergencyContacts.title")}</h5>
          </div>
          <motion.div
            className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium text-white shadow-sm ${
              sosActivated ? "bg-gradient-to-r from-green-500 to-emerald-600" : `bg-gradient-to-r ${color}`
            }`}
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: sosActivated ? 0.5 : 2, repeat: Infinity }}
          >
            <motion.span
              className="inline-block w-2 h-2 rounded-full bg-white ml-1"
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
            {sosActivated ? t("widgets.emergencyContacts.sosSent") : t("widgets.emergencyContacts.ready")}
          </motion.div>
        </div>

        {/* دکمه SOS اصلی */}
        <motion.button
          onClick={handleSOS}
          className={`w-full py-4 rounded-2xl text-white font-bold text-base relative overflow-hidden shadow-xl mb-4 ${
            sosActivated
              ? "bg-gradient-to-r from-green-500 to-emerald-600"
              : sending
              ? "bg-gradient-to-r from-amber-500 to-orange-500 cursor-not-allowed"
              : `bg-gradient-to-r ${color}`
          }`}
          whileHover={!sending && !sosActivated ? { scale: 1.03, boxShadow: "0 12px 30px rgba(244, 63, 94, 0.4)" } : {}}
          whileTap={!sending && !sosActivated ? { scale: 0.97 } : {}}
          animate={sosActivated ? {} : sending ? {} : { boxShadow: ["0 4px 15px rgba(244, 63, 94, 0.3)", "0 8px 25px rgba(244, 63, 94, 0.5)", "0 4px 15px rgba(244, 63, 94, 0.3)"] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {/* موج پالس */}
          {!sosActivated && !sending && (
            <>
              {[...Array(2)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute inset-0 rounded-2xl border-4 border-red-400"
                  animate={{ scale: [1, 1.2], opacity: [0.5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.75 }}
                />
              ))}
            </>
          )}
          <motion.span
            className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
            animate={sending ? { x: ["-100%", "100%"] } : { x: "-100%" }}
            transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
          />
          <span className="relative z-10 flex items-center justify-center gap-2">
            {sosActivated ? (
              <><CheckCircle className="w-6 h-6" /> {t("widgets.emergencyContacts.sosSentMessage")}</>
            ) : sending ? (
              <><motion.div animate={{ rotate: [0, 360] }} transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}><Radio className="w-6 h-6" /></motion.div> {t("widgets.emergencyContacts.sendingSOS")}</>
            ) : (
              <><Zap className="w-6 h-6" /> {t("widgets.emergencyContacts.sendSOS")}</>
            )}
          </span>
        </motion.button>

        {/* مخاطبین اضطراری */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <p className="text-gray-700 text-xs font-semibold">{t("widgets.emergencyContacts.contactsTitle")}</p>
            <span className="text-xs text-rose-500 font-medium">{t("widgets.emergencyContacts.personCount", { count: contacts.length })}</span>
          </div>
          <div className="space-y-2">
            {contacts.map((contact, idx) => (
              <motion.div
                key={idx}
                className={`flex items-center justify-between p-2.5 rounded-xl border shadow-sm ${
                  contact.primary ? "bg-rose-50 border-rose-200" : "bg-gray-50 border-gray-100"
                }`}
                whileHover={{ y: -1, boxShadow: "0 4px 12px rgba(0,0,0,0.08)", backgroundColor: "white" }}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * idx }}
              >
                <div className="flex items-center gap-2.5">
                  <div className="relative">
                    <div className={`w-8 h-8 ${contact.color} rounded-full flex items-center justify-center text-white font-bold text-sm`}>
                      {contact.avatar}
                    </div>
                    {contact.primary && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-400 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">★</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-800">{contact.name}</p>
                    <p className="text-xs text-gray-500">{contact.phone}</p>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <motion.button
                    className="w-7 h-7 bg-green-100 rounded-lg flex items-center justify-center text-green-600"
                    whileHover={{ scale: 1.1, backgroundColor: "rgb(220, 252, 231)" }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Phone className="w-3.5 h-3.5" />
                  </motion.button>
                  <motion.button
                    className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600"
                    whileHover={{ scale: 1.1, backgroundColor: "rgb(219, 234, 254)" }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* دکمه افزودن مخاطب */}
        <motion.button
          className={`w-full bg-gradient-to-r ${color} text-white py-2.5 rounded-xl text-sm font-semibold shadow-md relative overflow-hidden mb-3`}
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
        >
          <motion.span
            className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
            initial={{ x: "-100%" }}
            animate={{ x: "100%" }}
            transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
          />
          <span className="relative z-10 flex items-center justify-center gap-2">
            <UserPlus className="w-4 h-4" />
            {t("widgets.emergencyContacts.addContact")}
          </span>
        </motion.button>

        {/* پیام راهنما */}
        <motion.div
          className="bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200 text-rose-700 text-xs p-2 rounded-lg flex items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <motion.span
            className="ml-2 bg-rose-100 w-6 h-6 rounded-full flex items-center justify-center shrink-0"
            animate={{ rotate: [0, -10, 10, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            <AlertTriangle className="w-3.5 h-3.5" />
          </motion.span>
          <span>{t("widgets.emergencyContacts.tip")}</span>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default EmergencyContactsWidget;